import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ClipboardList, Edit, Trash2, Save, X, Plus, ArrowUp, ArrowDown, ChevronDown, ChevronRight, PlusCircle, MinusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Procedure Editor Component
const ProcedureEditor = ({ procedure, setProcedure, onSave, onCancel }) => {
  const [saving, setSaving] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState({});

  const handleSave = async () => { setSaving(true); await onSave(); setSaving(false); };
  const updateName = (value) => setProcedure({...procedure, name: value});
  const toggleStep = (index) => setExpandedSteps(prev => ({...prev, [index]: !prev[index]}));

  // Step handlers
  const updateStep = (index, field, value) => {
    const newSteps = [...(procedure.steps || [])];
    newSteps[index] = {...newSteps[index], [field]: value};
    setProcedure({...procedure, steps: newSteps});
  };
  const addStep = () => setProcedure({...procedure, steps: [...(procedure.steps || []), { text: "New step", sub_steps: [] }]});
  const removeStep = (index) => setProcedure({...procedure, steps: (procedure.steps || []).filter((_, i) => i !== index)});
  const moveStep = (index, direction) => {
    const newSteps = [...(procedure.steps || [])];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newSteps.length) return;
    [newSteps[index], newSteps[newIndex]] = [newSteps[newIndex], newSteps[index]];
    setProcedure({...procedure, steps: newSteps});
  };

  // Sub-step handlers
  const updateSubStep = (stepIndex, subIndex, value) => {
    const newSteps = [...(procedure.steps || [])];
    newSteps[stepIndex].sub_steps[subIndex] = value;
    setProcedure({...procedure, steps: newSteps});
  };
  const addSubStep = (stepIndex) => {
    const newSteps = [...(procedure.steps || [])];
    if (!newSteps[stepIndex].sub_steps) newSteps[stepIndex].sub_steps = [];
    newSteps[stepIndex].sub_steps.push("New sub-step");
    setProcedure({...procedure, steps: newSteps});
  };
  const removeSubStep = (stepIndex, subIndex) => {
    const newSteps = [...(procedure.steps || [])];
    newSteps[stepIndex].sub_steps = newSteps[stepIndex].sub_steps.filter((_, i) => i !== subIndex);
    setProcedure({...procedure, steps: newSteps});
  };

  // Info box handlers
  const updateInfoBox = (index, field, value) => {
    const newBoxes = [...(procedure.info_boxes || [])];
    newBoxes[index] = {...newBoxes[index], [field]: value};
    setProcedure({...procedure, info_boxes: newBoxes});
  };
  const addInfoBox = () => setProcedure({...procedure, info_boxes: [...(procedure.info_boxes || []), { type: "info", title: "New Info Box", content: "" }]});
  const removeInfoBox = (index) => setProcedure({...procedure, info_boxes: (procedure.info_boxes || []).filter((_, i) => i !== index)});

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={onCancel}><X className="w-4 h-4 mr-1" />Cancel</Button>
            <h1 className="text-xl font-heading font-bold flex items-center gap-2"><Edit className="w-5 h-5" />Edit Procedure</h1>
          </div>
          <Button onClick={handleSave} disabled={saving}><Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save Changes"}</Button>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-4xl space-y-6">
        <Card><CardHeader><CardTitle>Procedure Name</CardTitle></CardHeader><CardContent><Input value={procedure.name || ''} onChange={(e) => updateName(e.target.value)} className="text-lg font-medium" /></CardContent></Card>

        {/* Info Boxes */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Info Boxes ({procedure.info_boxes?.length || 0})</CardTitle>
              <Button size="sm" variant="outline" onClick={addInfoBox}><Plus className="w-4 h-4 mr-1" />Add Box</Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {(procedure.info_boxes || []).map((box, index) => (
              <div key={index} className={`border rounded-lg p-4 ${box.type === 'critical' ? 'border-red-500/50 bg-red-500/5' : box.type === 'warning' ? 'border-yellow-500/50 bg-yellow-500/5' : 'border-blue-500/50 bg-blue-500/5'}`}>
                <div className="flex items-start gap-2">
                  <div className="flex-1 space-y-2">
                    <div className="flex gap-2">
                      <select value={box.type} onChange={(e) => updateInfoBox(index, 'type', e.target.value)} className="px-2 py-1 rounded border bg-background text-sm">
                        <option value="info">Info (Blue)</option><option value="warning">Warning (Yellow)</option><option value="critical">Critical (Red)</option>
                      </select>
                      <Input value={box.title} onChange={(e) => updateInfoBox(index, 'title', e.target.value)} placeholder="Title" className="flex-1" />
                    </div>
                    <textarea value={box.content} onChange={(e) => updateInfoBox(index, 'content', e.target.value)} placeholder="Content" className="w-full min-h-[80px] p-2 rounded border bg-background text-sm" />
                  </div>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => removeInfoBox(index)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
            {(!procedure.info_boxes || procedure.info_boxes.length === 0) && <p className="text-muted-foreground text-center py-4">No info boxes.</p>}
          </CardContent>
        </Card>

        {/* Steps */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Procedure Steps ({procedure.steps?.length || 0})</CardTitle>
              <Button size="sm" variant="outline" onClick={addStep}><Plus className="w-4 h-4 mr-1" />Add Step</Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {(procedure.steps || []).map((step, stepIndex) => (
              <div key={stepIndex} className="border rounded-lg p-3 bg-card">
                <div className="flex items-start gap-2">
                  <div className="flex flex-col gap-1 pt-1">
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => moveStep(stepIndex, -1)} disabled={stepIndex === 0}><ArrowUp className="w-3 h-3" /></Button>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => moveStep(stepIndex, 1)} disabled={stepIndex === (procedure.steps?.length || 0) - 1}><ArrowDown className="w-3 h-3" /></Button>
                  </div>
                  <div className="flex-1">
                    <div className="flex gap-2 mb-2">
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => toggleStep(stepIndex)}>
                        {expandedSteps[stepIndex] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </Button>
                      <span className="text-muted-foreground text-sm pt-2 w-6">{stepIndex + 1}.</span>
                      <Input value={step.text} onChange={(e) => updateStep(stepIndex, 'text', e.target.value)} className="flex-1" />
                    </div>
                    {expandedSteps[stepIndex] && step.sub_steps && step.sub_steps.length > 0 && (
                      <div className="ml-16 space-y-2 mt-2">
                        {step.sub_steps.map((sub, subIndex) => (
                          <div key={subIndex} className="flex items-center gap-2">
                            <span className="text-muted-foreground text-xs">•</span>
                            <Input value={sub} onChange={(e) => updateSubStep(stepIndex, subIndex, e.target.value)} className="flex-1 text-sm" />
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => removeSubStep(stepIndex, subIndex)}><MinusCircle className="w-3 h-3" /></Button>
                          </div>
                        ))}
                      </div>
                    )}
                    {expandedSteps[stepIndex] && (
                      <Button size="sm" variant="ghost" className="ml-16 mt-1 text-xs" onClick={() => addSubStep(stepIndex)}><PlusCircle className="w-3 h-3 mr-1" />Add sub-step</Button>
                    )}
                  </div>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => removeStep(stepIndex)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
            {(!procedure.steps || procedure.steps.length === 0) && <p className="text-muted-foreground text-center py-4">No steps.</p>}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4 pt-4 border-t">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}><Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save Changes"}</Button>
        </div>
      </div>
    </div>
  );
};

// Admin Procedures Management
const AdminProcedures = () => {
  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingProcedure, setEditingProcedure] = useState(null);
  const { token } = useAuth();

  const fetchProcedures = async () => {
    try {
      const res = await axios.get(`${API}/procedures`);
      setProcedures(res.data);
    } catch (error) {
      toast.error("Failed to load procedures");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProcedures(); }, []);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete procedure "${name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/procedures/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setProcedures(procedures.filter(p => p.id !== id));
      toast.success("Procedure deleted");
    } catch (error) {
      toast.error("Failed to delete procedure");
    }
  };

  const handleEdit = (procedure) => setEditingProcedure(JSON.parse(JSON.stringify(procedure)));

  const handleSave = async () => {
    try {
      await axios.put(`${API}/admin/procedures/${editingProcedure.id}`, editingProcedure, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Procedure saved!");
      setEditingProcedure(null);
      fetchProcedures();
    } catch (error) {
      toast.error("Failed to save procedure");
    }
  };

  const filteredProcedures = procedures.filter(p => (p.name || '').toLowerCase().includes(searchQuery.toLowerCase()));

  if (loading) return <div className="p-8 text-center">Loading...</div>;
  if (editingProcedure) return <ProcedureEditor procedure={editingProcedure} setProcedure={setEditingProcedure} onSave={handleSave} onCancel={() => setEditingProcedure(null)} />;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-xl font-heading font-bold flex items-center gap-2"><ClipboardList className="w-5 h-5" />Manage Procedures ({procedures.length})</h1>
          <Link to="/admin/dashboard"><Button variant="outline">Back to Dashboard</Button></Link>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="mb-6"><Input placeholder="Search procedures..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="max-w-md" /></div>
        <div className="space-y-2">
          {filteredProcedures.map(procedure => (
            <Card key={procedure.id}>
              <CardContent className="py-4 flex items-center justify-between">
                <div>
                  <div className="font-medium">{procedure.name}</div>
                  <div className="text-sm text-muted-foreground">{procedure.steps?.length || 0} steps</div>
                </div>
                <div className="flex gap-2">
                  <Link to={`/procedure/${procedure.id}`}><Button variant="outline" size="sm">View</Button></Link>
                  <Button variant="secondary" size="sm" onClick={() => handleEdit(procedure)}><Edit className="w-4 h-4 mr-1" />Edit</Button>
                  <Button variant="destructive" size="sm" onClick={() => handleDelete(procedure.id, procedure.name)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminProcedures;
